import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { IGoogleLocation, IGoogleQueryLocation } from "~/types/api-types";
import { PlacePrediction } from "~/types/local-types";
import { autoComplete } from "~/utils/ListingSave/helpers";

export const getQueryString = (
  searchKeys: Record<string, string | undefined>,
): string => {
  return new URLSearchParams(
    Object.entries(searchKeys)
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => [key, value!]),
  ).toString();
};

const boundsToSearchArray = function (
  googleBoundsObj: google.maps.LatLngBounds,
) {
  let latLonStr = googleBoundsObj.toString();
  latLonStr = latLonStr.replace(/[()\s]/g, "");
  const latLonArr = latLonStr.split(",");
  // latLonArr result from Google: [swLat, swLng, neLat, neLng]
  const latNW = latLonArr[2]; //same as neLat
  const lonNW = latLonArr[1]; //same as swLng
  const latSE = latLonArr[0]; //same as swLat
  const lonSE = latLonArr[3]; //same as neLng
  // search takes: [lat-nw,lon-nw, lat-se, lon-se]
  const requestArray = [latNW, lonNW, latSE, lonSE];
  const stringArr = "[" + requestArray + "]";
  return stringArr;
};

const geoCode = async (placeId: string, location: string) => {
  return new Promise((resolve, reject) => {
    const geoCoder = () => new window.google.maps.Geocoder();
    geoCoder().geocode(
      { placeId },
      (results: google.maps.GeocoderResult[] | null) => {
        if (!results?.length) {
          // This is a remenant from an old code, but want to keep it around,
          // because I do not know when it would happen, or how results can be 0 items
          return reject();
        }
        const queryObj = {
          location: "",
          point_of_interest: "",
          bbox_of_interest: "",
          viewport_bbox: "",
          place_id: placeId,
        };
        queryObj.location = location.replace(/,\s/g, "--").replace(/\s/g, "-");
        const geom = results[0].geometry;
        const lat = geom.location.lat();
        const lng = geom.location.lng();
        const POITypes = ["ROOFTOP", "RANGE_INTERPOLATED"];
        if (-1 !== POITypes.indexOf(geom.location_type)) {
          queryObj.point_of_interest = `[${[lat, lng]}]`;
        } else if (geom.viewport) {
          queryObj.bbox_of_interest = boundsToSearchArray(geom.viewport);
        } else {
          return reject({
            results,
            queryObj,
            latitude: lat,
            longitude: lng,
          });
        }
        return resolve({
          results,
          queryObj,
          latitude: lat,
          longitude: lng,
        });
      },
    );
  });
};

/** Zero-pads a date component to the two digits the interval format requires. */
const toTwoDigits = (component: number): string =>
  String(component).padStart(2, "0");

/**
 * A date as the search service's local-time stamp: `YYYY-MM-DDTHH:mm:00`. Built from
 * local components, not `toISOString()`, which would shift the visitor's chosen hour.
 */
const toLocalTimestamp = (date: Date): string => {
  const year = date.getFullYear();
  const month = toTwoDigits(date.getMonth() + 1);
  const day = toTwoDigits(date.getDate());
  const hours = toTwoDigits(date.getHours());
  const minutes = toTwoDigits(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}:00`;
};

/**
 * The `availability_interval` parameter: `{lat},{lng},{start},{end}`, empty unless both
 * bounds are present so callers can append it unconditionally. Commas stay literal.
 */
export const formatAvailabilityInterval = ({
  startDate,
  endDate,
  latitude,
  longitude,
}: {
  startDate?: Date;
  endDate?: Date;
  latitude: number;
  longitude: number;
}): string => {
  if (!startDate || !endDate) {
    return "";
  }

  return `${latitude},${longitude},${toLocalTimestamp(startDate)},${toLocalTimestamp(endDate)}`;
};

export const returnQueryString = (searchParams: IGoogleQueryLocation) => {
  const address = searchParams.location
    ? encodeURIComponent(
        searchParams.location.replace(/--/g, ", ").replace(/-/g, " "),
      )
    : undefined;

  const params = new URLSearchParams();

  if (searchParams.viewport_bbox) {
    params.set("viewport_bbox", searchParams.viewport_bbox);
  }
  if (searchParams.bbox_of_interest) {
    params.set("bbox_of_interest", searchParams.bbox_of_interest);
  }
  if (searchParams.point_of_interest) {
    params.set("point_of_interest", searchParams.point_of_interest);
  }

  // ffilter is always added.
  params.set("ffilter", "web-search");

  // Conditionally add address_bbox only if no bbox_of_interest and point_of_interest exist and address is truthy.
  if (
    !searchParams.bbox_of_interest &&
    !searchParams.point_of_interest &&
    address
  ) {
    params.set("address_bbox", address);
  }

  // Return the final query string.
  return params.toString();
};

interface GeoCodeResponse {
  queryObj: IGoogleQueryLocation;
  latitude: number;
  longitude: number;
}

const fetchGeoCode = async (selectedMapSuggestion: {
  id: string;
  text: string;
}) => {
  const label = get(selectedMapSuggestion, "text");
  const placeId = get(selectedMapSuggestion, "id");
  const { queryObj, latitude, longitude } = (await geoCode(
    placeId,
    label,
  )) as GeoCodeResponse;
  return { label, query: queryObj, latitude, longitude };
};

export const handleSearchSubmit = async (
  // Only `preventDefault` is used, so this asks for no more than that. React's
  // `FormEvent` satisfies it, and so does react-hook-form's `BaseSyntheticEvent` —
  // which the two types are otherwise incompatible over.
  e: Pick<React.FormEvent, "preventDefault">,
  {
    locationValue,
    onSearch,
    onUnresolved,
    setFetchingGeoData,
  }: {
    locationValue: { id: string; text: string };
    onSearch: (location: IGoogleLocation) => void;
    /**
     * The place resolved to nowhere to search, so `onSearch` will not run. A caller that
     * omits it cannot tell this apart from a search that worked.
     */
    onUnresolved?: (reason: unknown) => void;
    setFetchingGeoData: (loading: boolean) => void;
  },
) => {
  e.preventDefault();

  if (!locationValue || isEmpty(locationValue)) {
    setFetchingGeoData(false);
    onUnresolved?.(new Error("No place was chosen to geocode"));
    return;
  }
  let locationData: IGoogleLocation;
  try {
    locationData = await fetchGeoCode(locationValue);
  } catch (reason) {
    // `geoCode` rejects bare in one branch, so `reason` can be undefined.
    setFetchingGeoData(false);
    onUnresolved?.(reason);
    return;
  }
  const hasValue = !(
    isEmpty(locationData) ||
    isEmpty(locationData.query) ||
    !locationData.query.location
  );
  if (!hasValue) {
    setFetchingGeoData(false);
    onUnresolved?.(new Error("Geocoding returned no location to search"));
    return;
  }
  onSearch(locationData);
};

export const handleLocationSearch = async (
  value: string,
  GOOGLE_MAPS_BROWSER_KEY: string,
  setPredictions: (predictions: PlacePrediction[]) => void,
  setNoResults: (noResults: boolean) => void,
) => {
  await autoComplete(
    value,
    (predictions) =>
      setPredictions(predictions.map((p) => ({ ...p, reference: "" }))),
    setNoResults,
    GOOGLE_MAPS_BROWSER_KEY,
  );
};
